import React from "react";
import { Box, Typography, Select, Option, Input, Button } from "@mui/joy";
import { utcToLocal } from "../../../utils/date";
import { Activity } from "../../../api/assignments/Assignments.domain";
import { FailureReason, PossibleReturn } from "../../../api/rules/Rules.domain";

export interface ActivityEditorProps {
  activity: Activity;
  index: number;
  onChange: (index: number, field: string, value: string) => void;
  onChangeSpecificField: (index: number, fieldCode: string, value: string) => void;
  rules: {
    rules: any[];
    logisticsModeSequences: {
      [key: string]: string[];
    };
  };
  returnStatuses: PossibleReturn[];
  reasons: FailureReason[];
  isLastActivity: boolean;
  logisticsMode: string;
  activities: Activity[];
  activitiesChanged: boolean;
  /** Suggested values for specific fields (from other activities or additional characteristics). */
  suggestedValues?: Record<string, string>;
}

const FIELD_MAX_WIDTH = "clamp(320px, 90vw, 520px)";
const INLINE_MAX_WIDTH = "clamp(240px, 70vw, 500px)";

const ActivityEditor: React.FC<ActivityEditorProps> = ({
  activity,
  index,
  onChange,
  onChangeSpecificField,
  rules,
  returnStatuses,
  reasons,
  isLastActivity,
  logisticsMode,
  activities,
  activitiesChanged,
  suggestedValues = {},
}) => {
  const rule = rules.rules.find((rule) => rule.activityTypeCode === activity.activityType?.code);
  const activityLabel = rule?.activityTypeLabel || "Activité inconnue";

  const isExternalActivity = rule?.isExternalActivity;

  const isEditable = !isExternalActivity && activitiesChanged && isLastActivity;

  const isReadOnly = isExternalActivity || !isLastActivity;

  const sequence =
    rules.logisticsModeSequences[logisticsMode] || rules.logisticsModeSequences.DEFAULT;

  const previousActivity = index > 0 ? activities[index - 1] : null;
  const previousActivityRuleStatus = previousActivity?.responseDetail?.stepCode;

  const availableActivities = rules.rules.filter((rule) => {
    if (rule.isExternalActivity) return false;

    if (rule.conditionalRules?.triggers) {
      return rule.conditionalRules.triggers.some(
        (trigger: { previousActivity: string; whenStatus: string }) =>
          trigger.previousActivity === previousActivity?.activityType?.code &&
          trigger.whenStatus === previousActivityRuleStatus,
      );
    }

    const logisticRule = rule.logisticsModeRules?.[logisticsMode];
    if (logisticRule?.forbidden) return false;

    return sequence[index] === rule.activityTypeCode;
  });

  React.useEffect(() => {
    const currentStepCode = activity.responseDetail?.stepCode;
    if (
      availableActivities.length > 0 &&
      (!activity.activityType?.code || !activity.returnStatus || !currentStepCode)
    ) {
      const firstActivity = availableActivities[0];
      const firstPossibleReturn = firstActivity.possibleReturns?.[0];

      if (!activity.activityType?.code) {
        onChange(index, "activityTypeCode", firstActivity.activityTypeCode);
      }

      if ((!activity.returnStatus || !currentStepCode) && firstPossibleReturn) {
        // returnStatus: OK/KO ; stepCode: business code (RECEIVED_BY_CUSTOMER, FIXED, ...)
        onChange(index, "returnStatus", firstPossibleReturn.returnStatus);
        onChange(index, "stepCode", firstPossibleReturn.stepCode);
      }
    }
  }, [
    availableActivities,
    activity.activityType?.code,
    activity.returnStatus,
    activity.responseDetail?.stepCode,
    index,
    onChange,
  ]);

  const activityStepCode = activity.responseDetail?.stepCode;
  const selectedPossibleReturn =
    rule?.possibleReturns.find((pr: any) => pr.stepCode === activityStepCode) || null;

  const specificFields = selectedPossibleReturn?.specificFields || [];

  const displayedActivities = isExternalActivity ? rules.rules : availableActivities;

  // Filter options so no Option has undefined/null value (avoids MUI useListItem() "item is undefined")
  const validActivityOptions = displayedActivities.filter(
    (r) => r.activityTypeCode != null && r.activityTypeCode !== "",
  );
  const validReturnStatusOptions = returnStatuses.filter(
    (pr: any) => pr.stepCode != null && pr.stepCode !== "",
  );
  const validReasonOptions = reasons.filter(
    (r) => r.failureReasonCode != null && r.failureReasonCode !== "",
  );

  // Ensure Select value always matches a valid Option
  const activityTypeSelectValue = validActivityOptions.some(
    (r) => r.activityTypeCode === activity.activityType?.code,
  )
    ? (activity.activityType?.code ?? "")
    : (validActivityOptions[0]?.activityTypeCode ?? "");
  const stepCodeSelectValue = validReturnStatusOptions.some(
    (pr: any) => pr.stepCode === activityStepCode,
  )
    ? (activityStepCode ?? "")
    : (validReturnStatusOptions[0]?.stepCode ?? "");
  const reasonSelectValue = validReasonOptions.some(
    (r) => r.failureReasonCode === activity.stepFailureReason?.code,
  )
    ? (activity.stepFailureReason?.code ?? "")
    : (validReasonOptions[0]?.failureReasonCode ?? "");

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (!inputValue) {
      return;
    }

    const date = new Date(inputValue);

    if (isNaN(date.getTime())) {
      return;
    }

    const isoDate = inputValue + (inputValue.length === 16 ? ":00.000Z" : "");

    onChange(index, "issueDate", isoDate);
  };

  return (
    <Box
      key={index}
      ml={2}
      mt={2}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        p: 1,
        backgroundColor: isExternalActivity ? "neutral.softBg" : "background.surface",
        width: "100%",
        maxWidth: FIELD_MAX_WIDTH,
      }}
    >
      <Typography level="body-sm" fontWeight="bold">
        {`Activité #${index + 1}: ${activityLabel}`}
      </Typography>

      {isExternalActivity && (
        <Typography level="body-sm" sx={{ color: "neutral.700", mt: 1, fontWeight: "bold" }}>
          📋 Réponse SPB
        </Typography>
      )}

      {selectedPossibleReturn?.stop && (
        <Typography level="body-sm" sx={{ color: "danger.600", mt: 1, fontWeight: "bold" }}>
          ⚠️ Escalade SPB
        </Typography>
      )}

      <Box mt={1}>
        <Typography level="body-sm">Date :</Typography>
        <Input
          type="datetime-local"
          value={
            activity.issueDate && activity.issueDate.includes("T")
              ? utcToLocal(activity.issueDate)
              : ""
          }
          onChange={handleDateChange}
          sx={{ mt: 1, width: "100%", maxWidth: FIELD_MAX_WIDTH }}
          disabled={!isEditable}
        />
      </Box>

      <Box mt={1}>
        <Typography level="body-sm">Type d'activité :</Typography>
        {isReadOnly || validActivityOptions.length === 0 ? (
          <Input
            value={activityLabel}
            sx={{ mt: 1, width: "100%", maxWidth: FIELD_MAX_WIDTH }}
            readOnly
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
        ) : (
          <Select
            value={activityTypeSelectValue}
            onChange={(e, newVal) => {
              if (newVal != null && newVal !== "" && !isExternalActivity)
                onChange(index, "activityTypeCode", String(newVal));
            }}
            sx={{ mt: 1, width: "100%", maxWidth: FIELD_MAX_WIDTH }}
            slotProps={{
              root: {
                readOnly: isExternalActivity || !activitiesChanged,
              },
            }}
          >
            {validActivityOptions.map((rule) => (
              <Option key={rule.activityTypeCode} value={rule.activityTypeCode}>
                {rule.activityTypeLabel ?? rule.activityTypeCode}
              </Option>
            ))}
          </Select>
        )}
      </Box>

      <Box mt={1}>
        <Typography level="body-sm">Statut :</Typography>
        {isReadOnly || returnStatuses.length === 0 ? (
          <Input
            value={
              returnStatuses.find((s: any) => s.stepCode === activityStepCode)?.returnLabel ||
              activityStepCode ||
              ""
            }
            sx={{ mt: 1, width: "100%", maxWidth: FIELD_MAX_WIDTH }}
            readOnly
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
        ) : (
          <Select
            value={stepCodeSelectValue}
            onChange={(e, newVal) => {
              if (newVal == null || newVal === "" || isExternalActivity) return;
              const selected = validReturnStatusOptions.find((pr: any) => pr.stepCode === newVal);
              if (!selected) return;
              onChange(index, "returnStatus", selected.returnStatus);
              onChange(index, "stepCode", selected.stepCode);
            }}
            sx={{ mt: 1, width: "100%", maxWidth: FIELD_MAX_WIDTH }}
            slotProps={{
              root: {
                readOnly: isExternalActivity || !activitiesChanged,
              },
            }}
          >
            {validReturnStatusOptions.map((pr: any) => (
              <Option key={pr.stepCode} value={pr.stepCode}>
                {pr.returnLabel ?? pr.stepCode}
              </Option>
            ))}
          </Select>
        )}
      </Box>

      {reasons.length > 0 && (
        <Box mt={1}>
          <Typography level="body-sm">Motif :</Typography>
          {isReadOnly || validReasonOptions.length === 0 ? (
            <Input
              value={activity.stepFailureReason?.label || ""}
              sx={{ mt: 1, width: "100%", maxWidth: FIELD_MAX_WIDTH }}
              readOnly
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
            />
          ) : (
            <Select
              value={reasonSelectValue}
              onChange={(e, newVal) => {
                if (newVal != null && newVal !== "")
                  onChange(index, "stepFailureReasonCode", String(newVal));
              }}
              sx={{ mt: 1, width: "100%", maxWidth: FIELD_MAX_WIDTH }}
              slotProps={{
                root: {
                  readOnly: isExternalActivity || !activitiesChanged,
                },
              }}
            >
              {validReasonOptions.map((reason) => (
                <Option key={reason.failureReasonCode} value={reason.failureReasonCode}>
                  {reason.failureReasonLabel ?? reason.failureReasonCode}
                </Option>
              ))}
            </Select>
          )}
        </Box>
      )}

      {specificFields.length > 0 && (
        <Box mt={1}>
          <Typography level="body-sm">Informations complémentaires :</Typography>
          {specificFields.map((field: any) => {
            const currentValue =
              activity.responseDetail?.dynamicProperties?.find((prop) => prop.key === field.code)
                ?.value ?? "";
            const suggestion = suggestedValues[field.code];
            const showSuggestion =
              activitiesChanged &&
              !String(currentValue).trim() &&
              suggestion != null &&
              String(suggestion).trim() !== "";
            return (
              <Box key={field.code} mt={1}>
                <Typography level="body-sm">{field.label} :</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <Input
                    value={currentValue}
                    onChange={(e) => onChangeSpecificField(index, field.code, e.target.value)}
                    placeholder={showSuggestion ? String(suggestion) : undefined}
                    sx={{ flex: 1, maxWidth: INLINE_MAX_WIDTH }}
                    readOnly={!activitiesChanged}
                  />
                  {showSuggestion && (
                    <Button
                      size="sm"
                      variant="soft"
                      onClick={() =>
                        onChangeSpecificField(index, field.code, String(suggestion).trim())
                      }
                    >
                      Remplir
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Box mt={1}>
        <Typography level="body-sm">Commentaires :</Typography>
        <Input
          value={activity.responseDetail?.freeField || ""}
          onChange={(e) => onChangeSpecificField(index, "freeField", e.target.value)}
          sx={{ mt: 1, width: "100%", maxWidth: FIELD_MAX_WIDTH }}
          disabled={!isEditable}
          slotProps={{
            input: {
              readOnly: isExternalActivity || !activitiesChanged,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default React.memo(ActivityEditor);
